// User related types
export interface User {
  id: string;
  name: string;
  email: string;
  role?: UserRole | string;
  avatar?: string;
  avatar_url?: string | null;
  is_active?: boolean;
  is_verified?: boolean;
  roles?: string[];
  created_at?: string;
  updated_at?: string;
  last_login?: string;
  organization_role?: string;
  permissions?: Permission[];
}

export enum UserRole {
  Viewer = 'viewer',
  Owner = 'Owner',
  Admin = 'Admin',
  InvestmentAnalyst = 'investment_analyst',
  VP = 'vp',
  Partner = 'partner',
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: string;
  permissions: Permission[];
  is_active: boolean;
  is_primary: boolean;
  invited_by: string;
  joined_at: string;
  updated_at: string;
  user: User;
  user_name: string;
  user_email: string;
  user_avatar_url: string | null;
  invitation_status?: 'Pending' | 'Accepted' | null;
  department_id?: number | null;
  department_name?: string | null;
  department?: Department | null;
}

// Document related types
export interface Document {
  id: string;
  name: string;
  filename?: string;  // Added filename property to match API response
  source?: string;
  type: DocumentType | string;
  project_id: string;
  file_type?: string;
  uploadDate: string;
  created_at: string;
  uploadedBy: string;
  versions: DocumentVersion[];
  summary?: string;
  clauses: Clause[];
  status?: 'completed' | 'processing' | 'failed' | 'uploaded' | 'pending';
  processingProgress?: number;
  deal_id? : string;
  parent?: {
    id: string;
    name: string;
  };
  file_size?: number;
  uploader_name?: string | null;
  project_name?: string | null;
  mime_type?: string;
  uploaded_at?: string;
  processing_status?: 'pending' | 'completed' | 'processing' | 'failed' | 'uploaded';
  embedding_status?: 'pending' | 'completed' | 'processing' | 'failed';
  document_type?: string;  // Added to match API response
  ai_summary?: string | null;  // Added to match API response
  source_type?: 'project' | 'deal' | 'shared' | 'general'; // Added to indicate the source of the document
}

export interface DocumentStats {
  total_documents: number;
  processed: number;
  processing: number;
  total_size: number;
}

export enum DocumentSort {
    NAME_ASC = "name_asc",
    NAME_DESC = "name_desc",
    UPLOADED_ASC = "uploaded_asc",
    UPLOADED_DESC = "uploaded_desc",
    PROCESSED_ASC = "processed_asc",
    PROCESSED_DESC = "processed_desc",
    SIZE_ASC = "size_asc",
    SIZE_DESC = "size_desc",
}

export enum DocumentType {
  Lease = 'lease',
  JVAgreement = 'jv_agreement',
  PSA = 'psa',
  LoanDoc = 'loan_doc',
  OfferingMemorandum = 'offering_memorandum',
  SponsorModel = 'sponsor_model',
  Other = 'other',
}

export interface DocumentVersion {
  id: string;
  version: number;
  date: string;
  updatedBy: string;
}

// Clause related types
export interface Clause {
  id: string;
  type: ClauseType;
  summary: string;
  content: string;
  documentId: string;
  page?: number;
}

export enum ClauseType {
  RentEscalation = 'rent_escalation',
  TermLength = 'term_length',
  RenewalOption = 'renewal_option',
  TerminationRight = 'termination_right',
  MaintenanceResponsibility = 'maintenance_responsibility',
  SecurityDeposit = 'security_deposit',
  UseRestriction = 'use_restriction',
  AssignmentSubletting = 'assignment_subletting',
  Other = 'other',
}

// Lease Abstract related types
export interface LeaseAbstract {
  id: string;
  documentId: string;
  leaseStart: string;
  leaseEnd: string;
  rentPerSF: number;
  gla: number;
  escalationType: EscalationType;
  tenant: string;
  property: string;
  securityDeposit?: number;
  termRenewalOptions?: string;
  notes?: string;
}

export enum EscalationType {
  Fixed = 'fixed',
  CPI = 'cpi',
  Percentage = 'percentage',
  None = 'none',
}

// Investment Memo related types
export interface InvestmentMemo {
  id: string;
  title: string;
  createdDate: string;
  createdBy: string;
  lastModified: string;
  dealSummary: string;
  risks: string;
  terms: string;
  returns: string;
  supportingDocs: SupportingDocument[];
  version: number;
}

export interface SupportingDocument {
  id: string;
  name: string;
  type: SupportingDocType;
  uploadDate: string;
}

export enum SupportingDocType {
  OfferingMemorandum = 'offering_memorandum',
  Appraisal = 'appraisal',
  Model = 'model',
  Other = 'other',
}

// Dashboard/Visualization related types
export interface KeyMetric {
  id: string;
  name: string;
  value: number;
  unit: string;
  trend?: number;
}

export interface ChartData {
  id: string;
  title: string;
  type: ChartType;
  data: any; // This would be more specific based on the chart library
}

export enum ChartType {
  Line = 'line',
  Bar = 'bar',
  Pie = 'pie',
  Area = 'area',
}

// Template related types
export interface Template {
  id: string;
  name: string;
  type: TemplateType;
  content: string;
  createdBy: string;
  createdDate: string;
}

export enum TemplateType {
  Clause = 'clause',
  Report = 'report',
  Memo = 'memo',
  CapitalCall = 'capital_call',
  Distribution = 'distribution',
}

// Audit log related types
export interface AuditLog {
  id: string;
  user_id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  details: any | null;
  description: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  has_next: boolean;
  has_prev: boolean;
}

// Investment Committee related types
export interface ICMemo {
  id: string;
  dealName: string;
  assetName: string;
  fund: string;
  date: string;
  recommendation: RecommendationType;
  decision: DecisionType;
  summary: string;
  underwritingAssumptions: string[];
  attachments: ICAttachment[];
  votes: ICVote[];
  versions: ICMemoVersion[];
}

export enum RecommendationType {
  Buy = 'buy',
  Hold = 'hold',
  Sell = 'sell'
}

export enum DecisionType {
  Approved = 'approved',
  Rejected = 'rejected',
  Pending = 'pending'
}

export interface ICAttachment {
  id: string;
  name: string;
  type: string;
  url: string;
}

export interface ICVote {
  id: string;
  userId: string;
  userName: string;
  vote: VoteType;
  rationale: string;
  timestamp: string;
}

export enum VoteType {
  Yes = 'yes',
  No = 'no',
  Abstain = 'abstain'
}

export interface ICMemoVersion {
  id: string;
  version: number;
  date: string;
  updatedBy: string;
  changes: string[];
}

// Attribution & Deviations related types
export interface AttributionData {
  id: string;
  assetId: string;
  assetName: string;
  fund: string;
  underwrittenIRR: number;
  actualIRR: number;
  underwrittenMOIC: number;
  actualMOIC: number;
  underwrittenExitCap: number;
  actualExitCap: number;
  factors: AttributionFactor[];
  rootCauseNotes: string;
}

export interface AttributionFactor {
  id: string;
  name: string;
  impact: number;
  isPositive: boolean;
}

// Underwriting Support related types
export interface DealMetadata {
  id: string;
  name: string;
  location: string;
  propertyType: string;
  equitySize: number;
  debtTerms: string;
  uploadDate: string;
  documentId: string;
}

export interface SimilarDeal {
  id: string;
  name: string;
  similarityScore: number;
  location: string;
  propertyType: string;
  equitySize: number;
  irr: number;
  capRate: number;
  explanation: string;
}

export enum SeverityLevel {
  Critical = 'critical',
  Moderate = 'moderate',
  Low = 'low',
}

export interface RedFlag {
  id: string;
  issue: string;
  severity: SeverityLevel;
  explanation: string;
  dismissed: boolean;
}

export interface ModelIssue {
  id: string;
  issue: string;
  explanation: string;
  impact: string;
}

export interface BidEstimate {
  id: string;
  minBid: number;
  maxBid: number;
  targetIRR: number;
  holdPeriod: number;
  leverageConstraint: string;
  justification: string;
}

export interface RiskFactor {
  id: string;
  summary: string;
  impact: string;
  mitigationStrategy: string;
  liked: boolean | null;
}

export interface CMBSCreditData {
  id: string;
  debtStack: DebtStackLayer[];
  metrics: {
    refiRisk: number;
    dscrAtMaturity: number;
    exitCapRate: number;
    moic: number;
  };
  news: CMBSNews[];
}

export interface DebtStackLayer {
  id: string;
  type: 'Senior' | 'Mezzanine' | 'Equity';
  amount: number;
  percentage: number;
  ltv: number;
  dscr: number;
  riskNotes: string;
}

export interface CMBSNews {
  id: string;
  title: string;
  date: string;
  source: string;
  type: 'Tenant' | 'Market' | 'Rating';
  summary: string;
}

// Capital Activity related types
export enum CapitalCallStatus {
  Draft = 'draft',
  Issued = 'issued',
  Paid = 'paid',
  Overdue = 'overdue',
}

export interface CapitalCall {
  id: string;
  name: string;
  fundId: string;
  fundName: string;
  dateIssued: string;
  dueDate: string;
  amountRequested: number;
  status: CapitalCallStatus;
  pdfNoticeUrl?: string;
  createdBy: string;
  createdDate: string;
  lastModified: string;
  lastModifiedBy: string;
  notes?: string;
}

export enum DistributionType {
  Income = 'income',
  ReturnOfCapital = 'roc',
  CarriedInterest = 'carried_interest',
}

export enum DistributionStatus {
  Scheduled = 'scheduled',
  Completed = 'completed',
  Failed = 'failed',
}

export interface Distribution {
  id: string;
  fundId: string;
  fundName: string;
  distributionDate: string;
  type: DistributionType;
  amountDistributed: number;
  recipients: string;
  status: DistributionStatus;
  pdfNoticeUrl?: string;
  createdBy: string;
  createdDate: string;
  lastModified: string;
  lastModifiedBy: string;
  notes?: string;
}

// Investment Thesis related types
export interface FirmProfile {
  id: string;
  firm_name: string;
  fund_name?: string;
  sector_focus: string[];
  industry_focus: string[];
  stage_focus: string[];
  geographic_focus: string[];
  investment_size_min: string;
  investment_size_max: string;
  holding_period_target: number;
  holding_period_unit: 'months' | 'years';
  exit_strategy_preferences: string[];
  target_portfolio_mix?: Record<string, number>;
  organization_id: string;
  created_at: string;
  updated_at: string;
}

export interface InvestmentThesis {
  id: string;
  firm_profile_id: string;
  core_investment_themes: string[];
  value_creation_levers: string[];
  deal_breakers: string[];
  preferred_business_models: string[];
  macro_trends: string[];
  micro_trends: string[];
  risk_appetite: 'low' | 'medium' | 'high' | 'Low' | 'Medium' | 'High';
  name: string;
  description: string;
  organization_id: string;
  is_global: boolean;
  firm_profile: FirmProfile;
  fund_id: string | null;
  version: number;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  fund_name: string | null;
  creator_name: string | null;
}

export interface GlobalThesisPayload {
  name: string;
  description: string;
  core_investment_themes: string[];
  value_creation_levers: string[];
  deal_breakers: string[];
  preferred_business_models: string[];
  macro_trends: string[];
  micro_trends: string[];
  risk_appetite: 'low' | 'medium' | 'high';
  is_global: boolean;
  fund_id: string | null;
  sector_focus: string[];
  geographic_focus: string[];
  industry_focus: string[];
}

export interface DecisionCriteria {
  id: string;
  firmProfileId: string;
  categories: CriteriaCategory[];
  lastUpdated: string;
  updatedBy: string;
}

export interface CriteriaCategory {
  id: string;
  name: string;
  weight: number;
  criteria: Criterion[];
}

export interface Criterion {
  id: string;
  name: string;
  description?: string;
  thresholdType: 'Range' | 'Minimum' | 'Maximum' | 'Boolean' | 'Options';
  thresholdValue?: any;
  thresholdMin?: number;
  thresholdMax?: number;
  options?: string[];
  isMustMeet: boolean;
  weight: number;
}

export interface WorkflowProcess {
  id: string;
  firmProfileId: string;
  stages: WorkflowStage[];
  lastUpdated: string;
  updatedBy: string;
}

export interface WorkflowStage {
  id: string;
  name: string;
  order: number;
  entryRules: string[];
  exitRules: string[];
  roleAssignments: string[];
  stageSLA: number;
  stageSLAUnit: 'days' | 'weeks' | 'months';
  approvalRule: 'majority' | 'unanimous' | 'veto';
  isActive: boolean;
}

export interface EvaluationTemplate {
  id: string;
  firmProfileId: string;
  name: string;
  type: 'Initial Screening' | 'Detailed Evaluation' | 'Red Flag Checklist' | 'IC Pack';
  sections: TemplateSection[];
  lastUpdated: string;
  updatedBy: string;
}

export interface TemplateSection {
  id: string;
  name: string;
  order: number;
  fields: TemplateField[];
}

export interface TemplateField {
  id: string;
  name: string;
  type: 'Text' | 'Number' | 'Date' | 'Dropdown' | 'MultiSelect' | 'Checkbox' | 'Radio' | 'Textarea';
  required: boolean;
  options?: string[];
  defaultValue?: any;
  order: number;
}

export interface FilterRule {
  id: string;
  firmProfileId: string;
  name: string;
  description: string;
  filter_type: 'Auto-Accept' | 'Auto-Reject' | 'Highlight';
  conditions: RuleCondition[];
  action: string;
  is_active: boolean;
  lastUpdated: string;
  updatedBy: string;
}

export interface RuleCondition {
  id: string;
  field: string;
  operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'contains' | 'not_contains' | 'between';
  value: any;
  valueMax?: any;
  logicalOperator?: 'AND' | 'OR';
}

export interface ThesisVersion {
  id: string;
  firmProfileId: string;
  versionNumber: number;
  timestamp: string;
  userId: string;
  userName: string;
  changes: ThesisChange[];
  rationale?: string;
}

export interface ThesisChange {
  id: string;
  section: string;
  field: string;
  oldValue: any;
  newValue: any;
}

export interface ThesisComparison {
  differences: ComparisonChange[];
}

export interface ComparisonChange {
  field: string;
  old_value: any;
  new_value: any;
}

// Deal related types
export interface Deal {
  id: string;
  name: string;
  company: string | null;
  industry: string | null;
  source: string | null;
  description: string;
  deal_type: DealType;
  status: DealStatus;
  region: string;
  tags: string[];
  estimatedValue: number;
  currency: string;
  expectedCloseDate: string | null;
  actual_close_date: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  creator_name: string | null;
  investment_memos: any[]; // Define a proper type if you have it
  project_count: number;
  project_id?: string;
}

export enum DealType {
  Acquisition = 'acquisition',
  Investment = 'investment',
  Merger = 'merger',
  Divestiture = 'divestiture',
  JointVenture = 'joint_venture',
  Other = 'other',
}

export enum DealStatus {
  Pipeline = 'pipeline',
  UnderReview = 'under_review',
  DueDiligence = 'due_diligence',
  Negotiation = 'negotiation',
  Approved = 'approved',
  Closed = 'closed',
  Rejected = 'rejected',
  OnHold = 'on_hold',
  ConvertedToProject = 'converted_to_project',
}

export enum DealSort {
  CREATED_DESC = 'created_desc',
  CREATED_ASC = 'created_asc',
  UPDATED_DESC = 'updated_desc',
  UPDATED_ASC = 'updated_asc',
  NAME_ASC = 'name_asc',
  NAME_DESC = 'name_desc',
}

export enum FundStatus {
    FUNDRAISING = "fundraising",
    INVESTING = "investing",
    HARVESTING_RETURNS = "harvesting_returns",
    CLOSED = "closed",
}

export enum FundSort {
    NAME_ASC = "name_asc",
    NAME_DESC = "name_desc",
    VINTAGE_ASC = "vintage_asc",
    VINTAGE_DESC = "vintage_desc",
    COMMITMENTS_ASC = "commitments_asc",
    COMMITMENTS_DESC = "commitments_desc",
    IRR_ASC = "irr_asc",
    IRR_DESC = "irr_desc",
    MOIC_ASC = "moic_asc",
    MOIC_DESC = "moic_desc",
    CREATED_ASC = "created_asc",
    CREATED_DESC = "created_desc",
}

// Project Management related types
export interface Organization {
  id: string;
  name: string;
  description?: string;
  logo_url?: string | null;
  website?: string | null;
  organization_type?: string;
  timezone?: string;
  slug?: string;
  parent_id?: string | null;
  level?: string;
  status?: string;
  is_verified?: boolean;
  max_members?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  member_count?: number | null;
  children_count?: number | null;
  createdBy: string;
  createdDate: string;
  updatedBy: string;
  updatedDate: string;
  users: string[]; // User IDs
}

export enum ProjectLifecycle {
  InProcess = 'in_process',
  Completed = 'completed',
  Archived = 'archived',
  Deleted = 'deleted',
}

export enum ProjectStage {
  Origination = 'origination',
  Initiation = 'initiation',
  Planning = 'planning',
  Execution = 'execution',
  Monitoring = 'monitoring',
  Closure = 'closure',
}

// Project lifecycle transition tracking
export interface ProjectLifecycleTransition {
  id: string;
  projectId: string;
  fromLifecycle?: ProjectLifecycle;
  toLifecycle: ProjectLifecycle;
  fromStage?: ProjectStage;
  toStage?: ProjectStage;
  transitionDate: string;
  transitionBy: string;
  reason?: string;
  notes?: string;
}
export interface ProjectUser {
  user_id: string;
  role_id?: string;
  id: string;
  project_id: string;
  user: User;
  role?: string;
}

export interface ProjectTeamMember {
  id: string;
  userId: string;
  role: ProjectRole;
  permissions: ProjectPermissionType[];
  addedBy: string;
  addedDate: string;
  isActive: boolean;
}

export enum ProjectPermissionType {
  Read = 'read',
  Write = 'write',
  Delete = 'delete',
  Share = 'share',
  ManageTeam = 'manage_team',
  ManageSettings = 'manage_settings',
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  dealId?: string;
  deal?: Deal; // Add this line to include the deal object
  ownerId: string;
  owner?: User;
  createdBy?: string;
  createdDate?: string;
  created_at?: string;
  updatedBy?: string;
  updatedDate: string;
  updated_at?: string;
  lifecycle?: ProjectLifecycle;
  stage?: ProjectStage;
  priority?: ProjectPriority;
  tags: string[];
  isPrivate?: boolean;
  users: ProjectUser[];
  files?: ProjectFile[];
  folders?: ProjectFolder[];
  queries?: ProjectQuery[];
  fileCount: number;
  queryCount: number;
  totalSize: number;
  completedDate?: string;
  archivedDate?: string;
  archivedBy?: string;
  deletedDate?: string;
  deletedBy?: string;
  estimatedCompletionDate?: string;
  actualCompletionDate?: string;
  budget?: number;
  currency?: string;
  sharedWith?: string[];
}

export enum ProjectPriority {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  Critical = 'critical',
}

export enum ProjectSort {
  NAME_ASC = "name_asc",
  NAME_DESC = "name_desc",
  CREATED_ASC = "created_asc",
  CREATED_DESC = "created_desc",
  UPDATED_ASC = "updated_asc",
  UPDATED_DESC = "updated_desc",
  PRIORITY_ASC = "priority_asc",
  PRIORITY_DESC = "priority_desc",
  FILE_COUNT_ASC = "file_count_asc",
  FILE_COUNT_DESC = "file_count_desc",
}

export interface ProjectFile {
  id: string;
  name: string;
  type?: string;
  size?: number; // in bytes
  file_size?: number;
  uploadDate?: string;
  updatedDate?: string;
  uploaded_at?: string;
  uploadedBy?: string;
  path: string;
  parentFolderId?: string;
  tags?: string[];
  metadata?: Record<string, any>;
}

export interface ProjectFolder {
  id: string;
  name: string;
  parentFolderId?: string;
  createdBy: string;
  createdDate: string;
  updatedDate: string;
  path: string;
  fileCount: number;
  is_folder: boolean;
  children: (ProjectFile | ProjectFolder)[];
}

export interface ProjectQuery {
  id: string;
  name: string;
  description?: string;
  query: string;
  results?: any;
  createdBy: string;
  createdDate: string;
  lastRun?: string;
  status: QueryStatus;
  tags: string[];
  executionTime?: number; // in milliseconds
}

export enum QueryStatus {
  Draft = 'draft',
  Running = 'running',
  Completed = 'completed',
  Failed = 'failed',
  Pending = 'pending',
}

export interface ProjectPermission {
  id: string;
  projectId: string;
  userId: string;
  role: ProjectRole;
  grantedBy: string;
  grantedDate: string;
}

export enum ProjectRole {
  Viewer = 'viewer',
  Editor = 'editor',
  Admin = 'admin',
}

export interface ProjectActivity {
  id: string;
  projectId: string;
  userId: string;
  action: ProjectAction;
  timestamp: string;
  details: string;
  entityType?: string;
  entityId?: string;
}

export enum ProjectAction {
  Created = 'created',
  Updated = 'updated',
  Deleted = 'deleted',
  Shared = 'shared',
  FileUploaded = 'file_uploaded',
  FileDeleted = 'file_deleted',
  QueryCreated = 'query_created',
  QueryExecuted = 'query_executed',
  FolderCreated = 'folder_created',
}

// Integration related types
export interface Integration {
  id: string;
  name: string;
  category: IntegrationCategory;
  description?: string;
  logoUrl?: string;
  isConnected: boolean;
  connectedAt?: string;
  lastSync?: string;
  status: IntegrationStatus;
  settings?: IntegrationSettings;
}

export enum IntegrationCategory {
  CRM = 'CRM',
  RMS = 'RMS',
  DocumentRepository = 'Document Repository',
  PortfolioManagement = 'Portfolio Management',
  Compliance = 'Compliance',
  MarketIntelligence = 'Market Intelligence',
  PublicDataSources = 'Public Data Sources',
  Security = 'Security',
  Collaboration = 'Collaboration',
  Documentation = 'Documentation',
  LLM = 'LLM',
  REIT = 'REIT',
  PublicClouds = 'Public Clouds',
  Productivity = 'Productivity'
}

export enum IntegrationStatus {
  Connected = 'Connected',
  Disconnected = 'Disconnected',
  Error = 'Error',
  Syncing = 'Syncing'
}

export interface IntegrationSettings {
  apiKey?: string;
  endpoint?: string;
  syncFrequency?: string;
  enabledFeatures?: string[];
  customFields?: Record<string, any>;
}

export interface Fund {
  id: string;
  name: string;
  total_commitments: number;
  vintage: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface Department {
  id: number;
  name: string;
  organization_id: string;
}

export interface Permission {
  id: string;
  name: string;
  code: string;
}
